import type { ElementType } from "react";

import {
  ApproveIcon,
  CampaignsIcon,
  ChatIcon,
  DestinationIcon,
  FlagIcon,
  ImageIcon,
  MessagesIcon,
  MetricIcon,
  ProjectsIcon,
  SourceIcon,
  TraitIcon,
} from "@hightouch/htui";

import type {
  CommandResult,
  CommandResultType,
  CommandScope,
} from "src/components/commandbar/types";
import { CHANNELS } from "src/cep/messages/constants";
import { JOURNEY_MESSAGE_BASE } from "src/cep/messages/message-state";
import type {
  CommandBarObjectsQuery,
  CommandBarObjectsQueryVariables,
} from "src/graphql";
import {
  VISIBLE_AD_BRIEF_ASSET_FILTER,
  VISIBLE_AD_STUDIO_PROJECT_FILTER,
} from "src/pages/ad-studio/user-facing-project-filter";
import { RESOURCE_ICONS } from "src/pages/home/agentic/components/recently-viewed-list";
import { resolveSyncName } from "src/pages/home/agentic/components/recently-viewed-names";
import {
  buildChatSessionsFilter,
  type ChatSessionAgentType,
} from "src/pages/agents/constants";
import type { ChannelType } from "src/types/cep";
import { toIlikePattern } from "src/utils/ilike-pattern";

// Small enough per workspace to prefetch and match on the client.
export const INDEX_TYPES = [
  "sources",
  "destinations",
  "models",
  "audiences",
  "journeys",
  "projects",
] as const satisfies readonly CommandResultType[];

// Too many rows to prefetch, so only the server searches them.
export const SEARCH_TYPES = [
  "syncs",
  "ads",
  "ad-studio-projects",
  "native-delivery-campaigns",
  "native-delivery-messages",
  "tasks",
  "chats",
  "charts",
  "dashboards",
  "traits",
  "priority-lists",
  "metrics",
  "campaigns",
] as const satisfies readonly CommandResultType[];

export type IndexType = (typeof INDEX_TYPES)[number];
export type SearchType = (typeof SEARCH_TYPES)[number];
export type ObjectType = IndexType | SearchType;

export const OBJECT_TYPES: ObjectType[] = [...INDEX_TYPES, ...SEARCH_TYPES];

export const isSearchType = (type: CommandResultType): type is SearchType =>
  (SEARCH_TYPES as readonly CommandResultType[]).includes(type);

export const isObjectType = (type: CommandResultType): type is ObjectType =>
  (OBJECT_TYPES as readonly CommandResultType[]).includes(type);

// The list page each type lives under. A type is reachable when the nav links to it.
export const LIST_PATHS: Record<ObjectType, string> = {
  sources: "/sources",
  destinations: "/destinations",
  models: "/models",
  audiences: "/audiences",
  journeys: "/journeys",
  syncs: "/syncs",
  ads: "/ad-studio/projects",
  "ad-studio-projects": "/ad-studio/projects",
  "native-delivery-campaigns": "/native-delivery/campaigns",
  // Journey-triggered messages list under Campaigns when the nav hides Messages, so either page reaches them.
  "native-delivery-messages": "/native-delivery/campaigns",
  projects: "/projects",
  tasks: "/tasks",
  chats: "/agents/chats",
  charts: "/analytics",
  dashboards: "/dashboards",
  traits: "/traits",
  "priority-lists": "/priority-lists",
  metrics: "/metrics",
  campaigns: "/lifecycle-studio/projects",
};

const ICONS: Record<ObjectType, ElementType> = {
  sources: SourceIcon,
  destinations: DestinationIcon,
  models: RESOURCE_ICONS.models,
  audiences: RESOURCE_ICONS.audiences,
  journeys: RESOURCE_ICONS.journeys,
  syncs: RESOURCE_ICONS.syncs,
  ads: ImageIcon,
  "ad-studio-projects": RESOURCE_ICONS["ad-studio-projects"],
  "native-delivery-campaigns": CampaignsIcon,
  "native-delivery-messages": MessagesIcon,
  projects: ProjectsIcon,
  tasks: ApproveIcon,
  chats: ChatIcon,
  charts: RESOURCE_ICONS.charts,
  dashboards: RESOURCE_ICONS.dashboards,
  traits: TraitIcon,
  "priority-lists": FlagIcon,
  metrics: MetricIcon,
  campaigns: RESOURCE_ICONS.campaigns,
};

// Where a type's row goes on Enter: its list page, except ads, which live on their own page beside projects.
// The Messages page redirects to Campaigns when the nav hides it.
const PAGE_PATHS: Record<ObjectType, string> = {
  ...LIST_PATHS,
  ads: "/ad-studio/ads",
  "native-delivery-messages": "/native-delivery/messages",
};

// The type whose page a nav link opens, so that link's command stands for the type's scope.
export const typeForPage = (href: string): ObjectType | undefined =>
  OBJECT_TYPES.find((type) => PAGE_PATHS[type] === href);

export const toScopes = (types: ObjectType[]): CommandScope[] =>
  types.map((type) => ({ type, icon: ICONS[type], href: PAGE_PATHS[type] }));

type ObjectWheres = Pick<
  CommandBarObjectsQueryVariables,
  | "sourceWhere"
  | "destinationWhere"
  | "modelWhere"
  | "audienceWhere"
  | "journeyWhere"
  | "syncWhere"
  | "adWhere"
  | "projectWhere"
  | "campaignWhere"
  | "messageWhere"
  | "ampProjectWhere"
  | "taskWhere"
  | "chatWhere"
  | "chartWhere"
  | "dashboardWhere"
  | "traitWhere"
  | "priorityListWhere"
  | "metricWhere"
  | "lifecycleCampaignWhere"
>;

export const reachableTypes = (hrefs: Set<string>): Set<ObjectType> =>
  new Set(OBJECT_TYPES.filter((type) => hrefs.has(LIST_PATHS[type])));

// Each list page's base filter, narrowed to names containing every word of `term` in any order, as the palette matches.
// Names only: the palette ranks and highlights on the name, so other matches would look like misses.
export const buildObjectWheres = ({
  term,
  includeRealtimeAudiences,
  brandProfileId,
  userId,
  chatAgentTypes,
}: {
  term: string;
  includeRealtimeAudiences: boolean;
  brandProfileId?: string;
  userId?: string | number;
  /** The agent types the Chats panel lists, which depend on flags. */
  chatAgentTypes?: readonly ChatSessionAgentType[];
}): ObjectWheres => {
  const byBrand = brandProfileId
    ? { brand_profile_id: { _eq: brandProfileId } }
    : null;
  const eachWord = <T>(toFilter: (ilike: { _ilike: string }) => T): T[] =>
    term
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => toFilter({ _ilike: toIlikePattern(word) }));
  const byName = eachWord((ilike) => ({ name: ilike }));
  const byTitle = eachWord((ilike) => ({ title: ilike }));
  // An unnamed destination shows its definition's name, which isn't filterable. Its `type` slug ("googlesheets") is the closest field.
  const byDestinationName = eachWord((ilike) => ({
    _or: [{ name: ilike }, { type: ilike }],
  }));
  // A sync row reads "model → destination", so each word may match either side.
  const bySyncName = eachWord((ilike) => ({
    _or: [
      { segment: { name: ilike } },
      { destination: { name: ilike } },
      { destination: { type: ilike } },
    ],
  }));
  return {
    sourceWhere: {
      _and: [
        { setup_complete: { _eq: true }, is_demo: { _eq: false } },
        ...byName,
      ],
    },
    destinationWhere: {
      _and: [{ setup_complete: { _eq: true } }, ...byDestinationName],
    },
    modelWhere: {
      _and: [
        {
          is_schema: { _eq: false },
          is_general_model: { _eq: false },
          query_type: { _nin: ["visual", "journey-node"] },
          _not: { flow_message: {} },
        },
        ...byName,
      ],
    },
    audienceWhere: {
      _and: [
        {
          query_type: { _eq: "visual" },
          _not: { campaigns: {} },
          ...(includeRealtimeAudiences
            ? {}
            : { is_realtime_audience: { _eq: false } }),
        },
        ...byName,
      ],
    },
    journeyWhere: {
      _and: [{ is_latest: { _eq: true }, deleted: { _neq: true } }, ...byName],
    },
    syncWhere: bySyncName.length ? { _and: bySyncName } : {},
    // Nested under `_and` so the search never replaces a visibility filter's own `_or`.
    adWhere: {
      _and: [
        VISIBLE_AD_BRIEF_ASSET_FILTER,
        ...(byBrand ? [{ ad_brief: { ad_studio_project: byBrand } }] : []),
        ...eachWord((ilike) => ({ asset_name: ilike })),
      ],
    },
    projectWhere: {
      _and: [
        VISIBLE_AD_STUDIO_PROJECT_FILTER,
        ...(byBrand ? [byBrand] : []),
        ...byName,
      ],
    },
    // The Campaigns page lists every campaign, drafts included.
    campaignWhere: byName.length ? { _and: byName } : {},
    // Journey-triggered only, as the Campaigns page lists them: a campaign's own messages open from their campaign.
    messageWhere: {
      _and: [...JOURNEY_MESSAGE_BASE, { name: { _is_null: false } }, ...byName],
    },
    ampProjectWhere: {
      _and: [{ deleted_at: { _is_null: true } }, ...byTitle],
    },
    // Done tasks too, which the Tasks page shows behind a filter.
    taskWhere: {
      _and: [
        {
          deleted_at: { _is_null: true },
          parent_task_id: { _is_null: true },
          project: { deleted_at: { _is_null: true } },
        },
        ...byTitle,
      ],
    },
    chatWhere: {
      _and: [
        buildChatSessionsFilter({ userId, agentTypes: chatAgentTypes }),
        { deleted: { _eq: false } },
        ...byName,
      ],
    },
    chartWhere: { _and: [{ archived_at: { _is_null: true } }, ...byName] },
    dashboardWhere: byName.length ? { _and: byName } : {},
    // Active traits, whose page is the only one a row's id opens.
    traitWhere: {
      _and: [
        { is_template: { _eq: false }, archived_at: { _is_null: true } },
        ...byName,
      ],
    },
    priorityListWhere: byName.length ? { _and: byName } : {},
    metricWhere: {
      _and: [{ _not: { config: { _has_key: "predefinedMetric" } } }, ...byName],
    },
    // Private campaigns show only to HT admins in dev mode, so the palette leaves them out.
    lifecycleCampaignWhere: {
      _and: [
        { type: { _eq: "project" }, is_private: { _neq: true } },
        ...byName,
      ],
    },
  };
};

export const includeTypes = (include: (type: ObjectType) => boolean) => ({
  withSources: include("sources"),
  withDestinations: include("destinations"),
  withModels: include("models"),
  withAudiences: include("audiences"),
  withJourneys: include("journeys"),
  withSyncs: include("syncs"),
  withAds: include("ads"),
  withProjects: include("ad-studio-projects"),
  withCampaigns: include("native-delivery-campaigns"),
  withMessages: include("native-delivery-messages"),
  withAmpProjects: include("projects"),
  withTasks: include("tasks"),
  withChats: include("chats"),
  withCharts: include("charts"),
  withDashboards: include("dashboards"),
  withTraits: include("traits"),
  withPriorityLists: include("priority-lists"),
  withMetrics: include("metrics"),
  withLifecycleCampaigns: include("campaigns"),
});

const toResult = ({
  type,
  row,
  name,
  href,
  detail,
  keepsOrigin,
}: {
  type: ObjectType;
  row: { id: unknown; updated_at?: string | null };
  name: string | null | undefined;
  href: string;
  detail?: string | null;
  keepsOrigin?: boolean;
}): CommandResult[] =>
  name
    ? [
        {
          id: String(row.id),
          type,
          name,
          icon: ICONS[type],
          href,
          ...(row.updated_at ? { updatedAt: row.updated_at } : {}),
          ...(detail ? { detail } : {}),
          ...(keepsOrigin ? { keepsOrigin } : {}),
        },
      ]
    : [];

const byUpdatedAtDesc = (a: CommandResult, b: CommandResult) =>
  (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");

const ROOTS: Record<ObjectType, keyof CommandBarObjectsQuery> = {
  sources: "sources",
  destinations: "destinations",
  models: "models",
  audiences: "audiences",
  journeys: "journeys",
  syncs: "syncs",
  ads: "ad_brief_assets",
  "ad-studio-projects": "ad_studio_projects",
  "native-delivery-campaigns": "native_delivery_campaigns",
  "native-delivery-messages": "native_delivery_messages",
  projects: "amp_projects",
  tasks: "amp_tasks",
  chats: "llm_chat_sessions",
  charts: "saved_analytics_charts",
  dashboards: "dashboards",
  traits: "trait_definitions",
  "priority-lists": "priority_lists",
  metrics: "goals",
  campaigns: "marketing_campaigns",
};

const isChannel = (
  channel: string | null | undefined,
): channel is ChannelType => !!channel && Object.hasOwn(CHANNELS, channel);

// The channels a campaign's messages send on, as its list row shows them.
const channelLabels = (
  messages: Array<{ channel_type?: string | null }>,
): string =>
  [...new Set(messages.map((message) => message.channel_type))]
    .filter(isChannel)
    .map((channel) => CHANNELS[channel].label)
    .join(", ");

// Rows each type's root returned, counted before rows without a name are dropped.
export const rowCounts = (
  data: CommandBarObjectsQuery,
): Map<ObjectType, number> =>
  new Map(
    OBJECT_TYPES.flatMap((type) => {
      const rows = data[ROOTS[type]];
      return Array.isArray(rows) ? [[type, rows.length] as const] : [];
    }),
  );

export const toResults = (data: CommandBarObjectsQuery): CommandResult[] =>
  [
    ...(data.sources ?? []).flatMap((row) =>
      toResult({
        type: "sources",
        row,
        name: row.name,
        href: `/sources/${row.id}`,
      }),
    ),
    ...(data.destinations ?? []).flatMap((row) =>
      toResult({
        type: "destinations",
        row,
        name: row.name ?? row.definition?.name,
        href: `/destinations/${row.id}`,
        detail: row.definition?.name,
      }),
    ),
    ...(data.models ?? []).flatMap((row) =>
      toResult({
        type: "models",
        row,
        name: row.name,
        href: `/models/${row.id}`,
      }),
    ),
    ...(data.audiences ?? []).flatMap((row) =>
      toResult({
        type: "audiences",
        row,
        name: row.name,
        href: `/audiences/${row.id}`,
      }),
    ),
    ...(data.journeys ?? []).flatMap((row) =>
      toResult({
        type: "journeys",
        row,
        name: row.name,
        href: `/journeys/${row.id}`,
      }),
    ),
    ...(data.syncs ?? []).flatMap((row) =>
      toResult({
        type: "syncs",
        row,
        name: resolveSyncName([row], String(row.id)),
        href: `/syncs/${row.id}`,
        detail: row.segment?.name,
      }),
    ),
    ...(data.ad_brief_assets ?? []).flatMap((row) => {
      const projectId = row.ad_brief?.ad_studio_project_id;
      return projectId
        ? toResult({
            type: "ads",
            row,
            name: row.asset_name,
            href: `/ad-studio/projects/${projectId}/concepts/${row.ad_brief_id}/${row.id}`,
            // The concept modal's close returns to where the ad was opened from.
            keepsOrigin: true,
          })
        : [];
    }),
    ...(data.ad_studio_projects ?? []).flatMap((row) =>
      toResult({
        type: "ad-studio-projects",
        row,
        name: row.name,
        href: `/ad-studio/projects/${row.id}`,
      }),
    ),
    ...(data.native_delivery_campaigns ?? []).flatMap((row) =>
      toResult({
        type: "native-delivery-campaigns",
        row,
        name: row.name,
        href: `/native-delivery/campaigns/${row.id}`,
        detail: channelLabels(row.messages),
      }),
    ),
    ...(data.native_delivery_messages ?? []).flatMap((row) =>
      toResult({
        type: "native-delivery-messages",
        row,
        name: row.name,
        href: `/native-delivery/messages/${row.id}`,
        detail: channelLabels([row]),
      }),
    ),
    ...(data.amp_projects ?? []).flatMap((row) =>
      toResult({
        type: "projects",
        row,
        name: row.title,
        href: `/projects/${row.id}`,
      }),
    ),
    ...(data.amp_tasks ?? []).flatMap((row) =>
      toResult({
        type: "tasks",
        row,
        name: row.title,
        href: `/tasks/${row.id}`,
      }),
    ),
    ...(data.llm_chat_sessions ?? []).flatMap((row) =>
      toResult({
        type: "chats",
        row,
        name: row.name,
        href: `/agents/chats/${row.id}`,
      }),
    ),
    ...(data.saved_analytics_charts ?? []).flatMap((row) =>
      toResult({
        type: "charts",
        row,
        name: row.name,
        href: `/analytics/${row.id}`,
      }),
    ),
    ...(data.dashboards ?? []).flatMap((row) =>
      toResult({
        type: "dashboards",
        row,
        name: row.name,
        href: `/dashboards/${row.id}`,
      }),
    ),
    ...(data.trait_definitions ?? []).flatMap((row) =>
      toResult({
        type: "traits",
        row,
        name: row.name,
        href: `/traits/active/${row.id}`,
      }),
    ),
    ...(data.priority_lists ?? []).flatMap((row) =>
      toResult({
        type: "priority-lists",
        row,
        name: row.name,
        href: `/priority-lists/${row.id}`,
      }),
    ),
    ...(data.goals ?? []).flatMap((row) =>
      toResult({
        type: "metrics",
        row,
        name: row.name,
        href: `/metrics/${row.id}`,
      }),
    ),
    ...(data.marketing_campaigns ?? []).flatMap((row) =>
      toResult({
        type: "campaigns",
        row,
        name: row.name,
        href: `/lifecycle-studio/projects/${row.id}`,
      }),
    ),
  ].sort(byUpdatedAtDesc);

// The first `limit` results of each type, in order.
export const capPerType = (
  results: CommandResult[],
  limit: number,
): CommandResult[] => {
  const counts = new Map<string, number>();
  return results.filter((result) => {
    const count = counts.get(result.type) ?? 0;
    counts.set(result.type, count + 1);
    return count < limit;
  });
};
